import '../../styles/app.sass';
import './App.sass';

import React from 'react';
import {connect} from 'react-redux';

import {fetchChannels, resetAlternateResources, setActiveChannel} from '../../ducks/channels';
import {fetchMenu} from '../../ducks/menus';
import {fetchRootTaxonChildren} from '../../ducks/taxons';
import {fetchReviewsTotal} from '../../ducks/reviews';
import {retrievePersistedCart} from '../../ducks/cart';
import {authorizeToken} from '../../ducks/auth';
import {openModal} from '../../ducks/ui';

import * as Cookie from 'js-cookie';

import {Helmet} from 'react-helmet';
import {browserHistory} from 'react-router';

import {channelByPrefix} from '../../routes/prefixes';

import IntlProviderContainer from '../intl/IntlProviderContainer';
import withChannelFormatters from '../channelUtils/withChannelFormatters';
import Header from '../../components/header/Header';
import Footer from '../../components/footer/Footer';
import ModalsContainer from '../modalsContainer/ModalsContainer';
import ContactModal from '../../components/modals/ContactModal';
import CartModalContainer from '../cartModalContainer/CartModalContainer';
import AuthFlowModal from '../auth/modals/AuthFlowModal';
import Background from '../../components/background/Background';
import { analyticsClearEcommerce, analyticsViewCart } from '../../middlewares/analytics/actions';
import Intercom from '../../components/html/thirdParty/Intercom';


const FOOTER_MENU_CODE = 'footer_primary';

const DEFAULT_TITLE = 'Boosthive';

@withChannelFormatters
@connect(({menus, auth, channels, cart, taxons, reviews}) => ({
  channels,
  cart,
  token: auth.token,
  footerMenu: menus[FOOTER_MENU_CODE],
  rootTaxonChildren: taxons.root.children,
  reviewsTotal: reviews.total
}))
export default class App extends React.PureComponent {
  static preloadData = ({dispatch}, {channel, channelDetected, token, carts} = {}) =>
    dispatch(fetchChannels(channel, channelDetected))
      .then(() => dispatch(retrievePersistedCart(carts)))
      .then(() => token && dispatch(authorizeToken(token)))
      .then(
        () => App.preloadComponentData({dispatch}),
        (err) => App.preloadComponentData({dispatch}).then(() => {
          if (err.status !== 403) {
            throw err;
          }
        })
      );

  static preloadComponentData = ({dispatch, rootTaxonChildren, footerMenu, reviewsTotal}) => Promise.all([
    rootTaxonChildren === undefined && dispatch(fetchRootTaxonChildren()),
    footerMenu === undefined && dispatch(fetchMenu(FOOTER_MENU_CODE)),
    reviewsTotal === undefined && dispatch(fetchReviewsTotal())
  ]);

  static persistCart(cart, channel) {
    let carts;
    try {
      carts = JSON.parse(Cookie.get('carts'));
    } catch (e) {
      carts = {};
    }

    if (cart && cart.anonymous) {
      carts[channel] = {
        id: cart.id,
        token: cart.tokenValue
      };
    } else {
      carts[channel] = undefined;
    }

    if (Object.values(carts).some(it => !!it)) {
      Cookie.set('carts', JSON.stringify(carts), {expires: 365});
    } else {
      Cookie.remove('carts');
    }
  }

  componentDidMount() {
    App.preloadComponentData(this.props);

    const {channels, cart} = this.props;

    if (!channels.channelAutoDetected) {
      Cookie.set('channel', channels.activeChannel, {expires: 365});
    }

    if (channels.availableChannels) {
      App.persistCart(cart, channels.activeChannel);
    }
  }

  componentWillReceiveProps(nextProps, nextContext) {
    const {dispatch, channels, location} = nextProps;

    const [, maybeRoutePrefix] = location.pathname.split('/', 2);
    const maybeChannelByPrefix = channelByPrefix[maybeRoutePrefix];
    if (maybeChannelByPrefix && channels.activeChannel !== maybeChannelByPrefix) {
      dispatch(setActiveChannel(maybeChannelByPrefix));
    }

    if (location.pathname !== this.props.location.pathname) {
      dispatch(resetAlternateResources());
    }
  }

  componentDidUpdate(prevProps) {
    const {dispatch, channels, cart, token} = this.props;

    if (!channels.channelAutoDetected && channels.channelAutoDetected !== prevProps.channelAutoDetected) {
      Cookie.set('channel', channels.activeChannel, {expires: 365});
    }

    if (channels.activeChannel !== prevProps.channels.activeChannel) {
      Cookie.set('channel', channels.activeChannel, {expires: 365});

      return App.preloadData({dispatch}, {
        token: Cookie.get('token'),
        carts: Cookie.get('carts')
      });
    }

    if (cart !== prevProps.cart) {
      App.persistCart(cart, channels.activeChannel);
    }

    if (token !== prevProps.token) {
      if (token) {
        Cookie.set('token', token, {expires: 365});
      } else {
        Cookie.remove('token');
      }
    }
  }

  render() {
    const {dispatch, channels, token, cart, footerMenu, channelFormatters, children, location} = this.props;

    // The order-review page (/order/:token/review) uses a stripped-down
    // layout — logo-only header, no footer — so the anonymous, token-authorised
    // flow stays focused.
    const isMinimalLayout = /\/order\/[^/]+\/review\/?$/.test(location.pathname);

    return (
      <IntlProviderContainer>
        <div className="app">
          <Helmet
            titleTemplate={`%s - ${DEFAULT_TITLE}`}
            defaultTitle={DEFAULT_TITLE}
          >
            <html lang={channels.activeLocale.replace('_', '-')}/>
          </Helmet>

          {process.env.ZENDESK_CHAT_ID && (
            <script
              id="ze-snippet"
              src={`https://static.zdassets.com/ekr/snippet.js?key=${process.env.ZENDESK_CHAT_ID}`}
            />
          )}

          {process.env.INTERCOM_APP_ID && (
            <Intercom appId={process.env.INTERCOM_APP_ID} />
          )}

          <div className="app__container">
            <div className="app__header">
              {isMinimalLayout ? (
                <Header minimal/>
              ) : (
                <Header
                  loggedIn={!!token}
                  cartItemsCount={cart && cart.items && cart.items.length}
                  onProfileClick={() => browserHistory.push(channelFormatters.link('/profile'))}
                  onLoginClick={() => dispatch(openModal(AuthFlowModal))}
                  onContactClick={() => dispatch(openModal(ContactModal))}
                  onCartClick={() => {
                    dispatch(openModal(CartModalContainer));

                    if (cart) {
                      dispatch(analyticsClearEcommerce());
                      dispatch(analyticsViewCart(cart));
                    }
                  }}
                />
              )}
            </div>

            <main className="app__content">
              <Background/>
              {children}
            </main>

            {!isMinimalLayout && (
              <Footer
                menu={footerMenu && footerMenu
                  .filter(it => !!it.page && it.level > 0)
                  .sort((a, b) => a.position - b.position)}
              />
            )}
          </div>

          <ModalsContainer/>
        </div>
      </IntlProviderContainer>
    );
  }
}



// WEBPACK FOOTER //
// ./containers/app/App.js